import React, { useMemo } from 'react';
import T from 'prop-types';
// components
import ReactSelect from 'components/Select/ReactSelect';
import { FormattedMessage } from 'react-intl';
// utils
import classnames from 'classnames/bind';
import moment from 'moment';
// styles
import styles from './MonthDaySelect.module.scss';

const cn = classnames.bind(styles);

// Leap year, so February offers 29 days
const BASE_YEAR = 2000;

const pad = (number) => String(number).padStart(2, '0');

const daysInMonth = (month) => (month ? moment([BASE_YEAR, Number(month) - 1]).daysInMonth() : 31);

const getDayOptions = (month) => (
  Array.from({ length: daysInMonth(month) }, (_, index) => ({ value: pad(index + 1), label: String(index + 1) }))
);

const joinBounds = (month, day) => (month || day ? `${month}-${day}` : '');

// Month + day picker without a year. Value is 'MM-DD'; '' or a partial value while incomplete.
// Use it for recurring yearly dates (seasons, anniversaries) where the year does not matter.
const MonthDaySelect = ({ value, onChange, className, disabled }) => {
  const [month = '', day = ''] = (value || '').split('-');
  const locale = moment.locale();
  // Month names follow the active moment locale
  const monthOptions = useMemo(
    () => moment.months().map((label, index) => ({ value: pad(index + 1), label })),
    [locale]
  );
  const dayOptions = useMemo(() => getDayOptions(month), [month]);

  const onMonthChange = (option) => {
    const nextMonth = option ? option.value : '';
    // Drop the day when it does not exist in the new month (e.g. 31 → February)
    const nextDay = day && Number(day) <= daysInMonth(nextMonth) ? day : '';
    onChange(joinBounds(nextMonth, nextDay));
  };

  const onDayChange = (option) => onChange(joinBounds(month, option ? option.value : ''));

  return (
    <div className={cn('month-day-select', className)}>
      <ReactSelect
        className={cn('month')}
        noInputMargin
        options={monthOptions}
        placeholder={<FormattedMessage id="component.monthDaySelect.month" />}
        value={month}
        onChange={onMonthChange}
        labelKey="label"
        matchProp="value"
        clearable={false}
        searchable={false}
        disabled={disabled}
      />
      <ReactSelect
        className={cn('day')}
        noInputMargin
        options={dayOptions}
        placeholder={<FormattedMessage id="component.monthDaySelect.day" />}
        value={day}
        onChange={onDayChange}
        labelKey="label"
        matchProp="value"
        clearable={false}
        searchable={false}
        disabled={disabled}
      />
    </div>
  );
};

MonthDaySelect.propTypes = {
  value: T.string,
  onChange: T.func.isRequired,
  className: T.string,
  disabled: T.bool,
};

MonthDaySelect.defaultProps = {
  value: '',
  className: '',
  disabled: false,
};

export default MonthDaySelect;
